import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as service from '@/services/groupMembershipsService';
import type { GroupMembership } from '@/types/models';

const KEY = 'groupMemberships';

export function useGroupMembers(groupId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'group', groupId],
    queryFn: () => service.getMembershipsByGroup(groupId!),
    enabled: !!groupId,
  });
}

export function useChurchMemberships(churchId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'church', churchId],
    queryFn: () => service.getMembershipsByChurch(churchId!),
    enabled: !!churchId,
  });
}

export function useUserGroups(userId: string | undefined, churchId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'user', userId, churchId],
    queryFn: () => service.getMembershipsByUser(userId!, churchId!),
    enabled: !!userId && !!churchId,
  });
}

export function useAddMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<GroupMembership, 'id'>) =>
      service.addMembership(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useUpdateMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Pick<GroupMembership, 'isGroupAdmin'>> }) =>
      service.updateMembership(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}

export function useRemoveMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => service.removeMembership(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
